import * as THREE from 'three';

/** The mysterious steampunk saucer that intercepts anyone who strays past the mountain ring.
 *  Not an Entity — it's a scripted set-piece owned by the Game's UFO encounter, added straight
 *  to the scene and driven frame-by-frame (spin + glow scale with the approach `intensity`).
 *  Brass hull, riveted rim, a glass observation dome, a rotating ring of running lights, and a
 *  glowing tractor emitter underneath that brightens as it locks on. */
export class Ufo {
  readonly group = new THREE.Group();
  private lightRing = new THREE.Group();   // spinning ring of running lights
  private turbine = new THREE.Group();     // counter-rotating underside turbine
  private beamCore: THREE.Mesh;            // tractor emitter — emissive rises with intensity
  private beamMat: THREE.MeshStandardMaterial;
  private domeMat: THREE.MeshStandardMaterial;
  private spin = 0;

  constructor() {
    const brass = new THREE.MeshStandardMaterial({ color: 0xb5892f, roughness: 0.35, metalness: 0.85, flatShading: true });
    const brassDk = new THREE.MeshStandardMaterial({ color: 0x6e521c, roughness: 0.5, metalness: 0.8, flatShading: true });
    const iron = new THREE.MeshStandardMaterial({ color: 0x2a2622, roughness: 0.6, metalness: 0.6, flatShading: true });
    const copper = new THREE.MeshStandardMaterial({ color: 0xc7743a, roughness: 0.4, metalness: 0.8 });

    // --- Hull: classic saucer from two cones base-to-base, flattened. ---
    const upper = new THREE.Mesh(new THREE.ConeGeometry(26, 9, 40, 1), brass);
    upper.position.y = 4.5;
    this.group.add(upper);
    const lower = new THREE.Mesh(new THREE.ConeGeometry(26, 13, 40, 1), brassDk);
    lower.rotation.x = Math.PI; // point down
    lower.position.y = -6.5;
    this.group.add(lower);

    // Riveted equator rim — a fat brass torus with a ring of rivets.
    const rim = new THREE.Mesh(new THREE.TorusGeometry(26, 2.4, 10, 44), brass);
    rim.rotation.x = Math.PI / 2;
    this.group.add(rim);
    for (let i = 0; i < 32; i++) {
      const a = (i / 32) * Math.PI * 2;
      const rivet = new THREE.Mesh(new THREE.SphereGeometry(0.7, 6, 5), copper);
      rivet.position.set(Math.cos(a) * 28, 0, Math.sin(a) * 28);
      this.group.add(rivet);
    }

    // --- Glass observation dome + brass collar. ---
    const collar = new THREE.Mesh(new THREE.CylinderGeometry(11.5, 12.5, 1.6, 32), brass);
    collar.position.y = 8.4;
    this.group.add(collar);
    this.domeMat = new THREE.MeshStandardMaterial({
      color: 0x2a4a55, roughness: 0.15, metalness: 0.2, transparent: true, opacity: 0.6,
      emissive: 0x1a6a7a, emissiveIntensity: 0.5,
    });
    const dome = new THREE.Mesh(new THREE.SphereGeometry(11, 24, 16, 0, Math.PI * 2, 0, Math.PI / 2), this.domeMat);
    dome.position.y = 9;
    this.group.add(dome);
    // Meridian brass ribs on the dome (steampunk framing).
    for (let i = 0; i < 4; i++) {
      const rib = new THREE.Mesh(new THREE.TorusGeometry(11, 0.35, 6, 20, Math.PI), brass);
      rib.position.y = 9; rib.rotation.y = (i / 4) * Math.PI;
      this.group.add(rib);
    }

    // --- Chimneys / exhaust stacks on the upper hull (unmistakably steam-powered). ---
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2 + 0.5;
      const stack = new THREE.Mesh(new THREE.CylinderGeometry(1.3, 1.7, 5, 10), iron);
      stack.position.set(Math.cos(a) * 16, 6.5, Math.sin(a) * 16);
      this.group.add(stack);
      const cap = new THREE.Mesh(new THREE.TorusGeometry(1.5, 0.4, 6, 12), copper);
      cap.rotation.x = Math.PI / 2; cap.position.set(Math.cos(a) * 16, 9, Math.sin(a) * 16);
      this.group.add(cap);
    }

    // --- Glowing amber portholes around the rim (static, part of the hull). ---
    const portholeGlow = new THREE.MeshBasicMaterial({ color: 0xffb347 });
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const p = new THREE.Mesh(new THREE.CircleGeometry(1.5, 12), portholeGlow);
      p.position.set(Math.cos(a) * 24, 1.2, Math.sin(a) * 24);
      p.lookAt(Math.cos(a) * 60, 6, Math.sin(a) * 60);
      this.group.add(p);
    }

    // --- Rotating ring of running lights (spins with intensity). ---
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      const col = i % 2 ? 0x66ddff : 0xff5a3a;
      const bulb = new THREE.Mesh(new THREE.SphereGeometry(1.4, 8, 6), new THREE.MeshBasicMaterial({ color: col }));
      bulb.position.set(Math.cos(a) * 20, -6, Math.sin(a) * 20);
      this.lightRing.add(bulb);
    }
    this.group.add(this.lightRing);

    // --- Underside turbine + tractor-beam emitter (glows as it locks on). ---
    const hub = new THREE.Mesh(new THREE.CylinderGeometry(6, 8, 4, 24), iron);
    hub.position.y = -11.5;
    this.group.add(hub);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const blade = new THREE.Mesh(new THREE.BoxGeometry(7, 0.5, 2.4), brassDk);
      blade.position.set(Math.cos(a) * 5, -13, Math.sin(a) * 5);
      blade.rotation.y = -a;
      this.turbine.add(blade);
    }
    this.group.add(this.turbine);
    this.beamMat = new THREE.MeshStandardMaterial({ color: 0x66ddff, emissive: 0x66ddff, emissiveIntensity: 1, transparent: true, opacity: 0.85 });
    this.beamCore = new THREE.Mesh(new THREE.ConeGeometry(4.5, 8, 20), this.beamMat);
    this.beamCore.rotation.x = Math.PI; // taper downward
    this.beamCore.position.y = -15;
    this.group.add(this.beamCore);
  }

  /** Spin the rings/turbine and brighten the glow. `intensity` 0..1 = how close to the peak. */
  update(dt: number, intensity: number) {
    this.spin += dt * (1.5 + intensity * 9);
    this.lightRing.rotation.y = this.spin;
    this.turbine.rotation.y = -this.spin * 1.6;
    const glow = 0.6 + intensity * 3.2;
    this.beamMat.emissiveIntensity = glow;
    this.beamCore.scale.setScalar(1 + intensity * 0.8);
    this.domeMat.emissiveIntensity = 0.4 + intensity * 1.6;
    const flicker = 0.7 + 0.3 * Math.sin(this.spin * 5);
    this.beamMat.opacity = 0.65 + 0.35 * flicker;
  }

  dispose() {
    this.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.geometry?.dispose?.();
        const mat = m.material as THREE.Material | THREE.Material[];
        if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
        else mat?.dispose?.();
      }
    });
    this.group.removeFromParent();
  }
}
